import React, { useState, useEffect } from 'react';

import { Typography, Grid, Button } from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import useMediaQuery from '@material-ui/core/useMediaQuery';

import { motion, useAnimation } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

import StatSection1 from './WhyDoesStat1';
import StatSection2 from './WhyDoesStat2';
import cityCouncil from '../assets/nycitycounsil.jpg';
import playButton from '../assets/playAudio.svg';
import pauseButton from '../assets/pauseAudio.svg';
import playButton3 from '../assets/playAudio3.svg';

import erikaAudio from '../assets/audio/whyDoesErika.wav';
import pattiAudio from '../assets/audio/whyDoesPatti.wav';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    marginTop: '6.25rem'
  },
  imgCont: {
    width: '51%',
    flexWrap: 'nowrap',
    marginBottom: '2.2rem',
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      flexWrap: 'wrap'
    }
  },
  imgCap: {
    width: '100%',
    marginTop: '1rem',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      width: '75%',
      marginTop: '1rem',
      textAlign: 'center'
    }
  },
  captions: {
    width: '18%',
    [theme.breakpoints.down('sm')]: {
      display: 'none'
    }
  },
  imgCap2: {
    width: '100',
    // alignSelf: 'flex-end',
    marginLeft: '20px',
    textAlign: 'left',
    [theme.breakpoints.down('sm')]: {
      // display: 'none's
    }
  },
  textCont: {
    width: '51%',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  textHeader: {
    width: '80%',
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  paragraphs: {
    width: '80%',
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  paragraph: {
    width: '80%',
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  boldYellow: {
    color: theme.palette.common.yellow,
    fontFamily: 'proximaNovaB'
  },
  linkText: {
    color: theme.palette.common.blue
  },
  infoGraphCont: {
    marginTop: '3.125rem',
    width: '51%',
    maxWidth: '600px',
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  infoGraphTit: {
    ...theme.extraFonts.insertTitle
  },
  barNLabel: {
    marginTop: '1.25rem'
  },
  graphicImg: {
    marginLeft: '1rem',
    height: '40px',
    [theme.breakpoints.down('sm')]: {
      marginLeft: 0
    }
  },
  graphic1: {
    [theme.breakpoints.down('sm')]: {
      maxWidth: '278px'
    }
  },
  button: {
    width: '88%',
    padding: 0,
    marginTop: '.75rem',

    '&:hover': {
      transform: 'scale(1.1,1.1)'
    }
  },
  infoGraphText: {
    textAlign: 'center',
    marginTop: '2rem',
    [theme.breakpoints.down('sm')]: {
      textAlign: 'left'
    }
  },
  lastTextBox: {
    textAlign: 'center',
    marginTop: '3.125rem',
    width: '51%',
    [theme.breakpoints.down('sm')]: {
      width: '75%'
    }
  },
  button1: {
    padding: 0,
    '&:hover': {
      backgroundColor: 'transparent',
      boxShadow: 'none'
    }
  },
  // ------------------------------------ mobileCap

  mobileWrapper: {
    flexWrap: 'nowrap',
    overflowX: 'scroll'
  },
  mobileAudioCapCont: {
    marginTop: '3.125rem',
    minWidth: '278px',
    textAlign: 'center',
    padding: '2rem 0',
    borderTop: '1px solid black',
    borderBottom: '1px solid black'
  },
  buttonMobile: {
    width: '40%'
  },
  mobileCaptionText: {
    height: '150px'
  }
}));

const nameToAudio = {
  erika: new Audio(erikaAudio),
  patti: new Audio(pattiAudio)
};

const WhyDoes = () => {
  const classes = useStyles();
  const theme = useTheme();
  const matchesSM = useMediaQuery(theme.breakpoints.down('sm'));
  const [isPlaying, setIsPlaying] = useState(false);
  const [personPlaying, setPersonPlaying] = useState('');

  const { ref, inView } = useInView();
  const animation = useAnimation();

  useEffect(() => {
    if (inView) {
      animation.start({
        opacity: 1,
        transition: {
          type: 'spring',
          duration: 3
        }
      });
    }
    if (!inView) {
      animation.start({ opacity: 0 });
    }
  }, [inView, animation]);

  const audioClickHandler = (name) => {
    if (isPlaying) {
      nameToAudio[personPlaying].pause();
      setIsPlaying(false);
      if (personPlaying !== name) {
        nameToAudio[name].play();
        setIsPlaying(true);
      }
    } else {
      nameToAudio[name].play();
      setIsPlaying(true);
    }
  };

  const desktopCap = (
    <Grid container direction="column" className={classes.captions}>
      <Grid item className={classes.imgCap2}>
        <Typography variant="caption">
          “The reason that Black women have been designated as high risk for breast cancer…because
          of the current landscape of racial disparities …in the United States.”
          <br />
          <span style={{ fontFamily: 'proximaNovaB' }}>Erika Stallings</span>
        </Typography>
        <Button
          disableFocusRipple
          disableRipple
          classes={{ root: classes.button1 }}
          onClick={() => {
            audioClickHandler('erika');
            setPersonPlaying('erika');
          }}
        >
          <img
            src={isPlaying && personPlaying === 'erika' ? pauseButton : playButton3}
            alt="playButton"
            className={classes.button}
            style={{ width: '100%' }}
          />
        </Button>
      </Grid>
      <Grid item className={classes.imgCap2} style={{ marginTop: '2rem' }}>
        <Typography variant="caption">
          "Latinas are the most affected by cervical cancer and there are two major culprits..."
          <br />
          <span style={{ fontFamily: 'proximaNovaB' }}>Patti Murillo-Casa</span>
        </Typography>
        <Button
          disableFocusRipple
          disableRipple
          className={classes.button1}
          onClick={() => {
            audioClickHandler('patti');
            setPersonPlaying('patti');
          }}
        >
          <img
            className={classes.button}
            src={isPlaying && personPlaying === 'patti' ? pauseButton : playButton3}
            alt="playButton"
            style={{ width: '100%' }}
          />
        </Button>
      </Grid>
    </Grid>
  );

  const mobileAudioCaption = (
    <Grid container className={classes.mobileWrapper}>
      {/* ------------------------------------------------------------------------------ patti  */}
      <Grid container justifyContent="center" className={classes.mobileAudioCapCont}>
        <Typography variant="body2" className={classes.mobileCaptionText}>
          “The reason that Black women have been designated as high risk for breast cancer…because
          of the current landscape of racial disparities …in the United States.”
          <br />
          <span className={classes.boldFont}>Erika Stalings</span>
        </Typography>
        <Button
          className={classes.buttonMobile}
          onClick={() => {
            audioClickHandler('erika');
            setPersonPlaying('erika');
          }}
        >
          <img
            src={isPlaying && personPlaying === 'erika' ? pauseButton : playButton}
            alt="playButton"
            style={{ width: '100%' }}
          />
        </Button>
      </Grid>
      {/* ------------------------------------------------------------------------------ karen  */}
      <Grid
        container
        justifyContent="center"
        className={classes.mobileAudioCapCont}
        style={{ marginLeft: '1rem', marginRight: '1rem' }}
      >
        <Typography variant="body2" className={classes.mobileCaptionText}>
          "Latinas are the most affected by cervical cancer and there are two major culprits..."
          <br />
          <span className={classes.boldFont}>Patti Murillo-Casa</span>
        </Typography>
        <Button
          className={classes.buttonMobile}
          onClick={() => {
            audioClickHandler('patti');
            setPersonPlaying('patti');
          }}
        >
          <img
            src={isPlaying && personPlaying === 'patti' ? pauseButton : playButton}
            alt="playButton"
            style={{ width: '100%' }}
          />
        </Button>
      </Grid>
    </Grid>
  );

  return (
    <Grid
      container
      justifyContent="center"
      alignItems="center"
      direction="column"
      className={classes.wrapper}
    >
      <Grid
        ref={ref}
        container
        direction="column"
        alignItems={matchesSM ? 'center' : 'flex-start'}
        className={classes.imgCont}
      >
        <motion.img
          animate={animation}
          src={cityCouncil}
          alt="people sitting around a table"
          style={{ width: '100%' }}
        />
        <Typography variant="caption" className={classes.imgCap}>
          Patti Murillo-Casa during a community convervation about cervical cancer and healthcare
          advocacy.
        </Typography>
      </Grid>
      <Grid
        container
        justifyContent={matchesSM ? 'center' : 'flex-start'}
        className={classes.textCont}
      >
        <Grid
          container
          item
          justifyContent={matchesSM ? 'center' : 'flex-start'}
          style={{ width: '100%' }}
        >
          <Typography variant="h2" className={classes.textHeader}>
            Why Does Cancer Seem to Affect Black and Hispanic Women Differently?
          </Typography>
        </Grid>
        <Grid
          container
          justifyContent={matchesSM ? 'center' : 'flex-start'}
          style={{ marginTop: '2.6rem' }}
        >
          <Grid container direction="row" className={classes.paragraphs}>
            <Typography variant="body1" className={classes.paragraph}>
              Researchers acknowledge that more comprehensive studies are needed to better
              understand the factors that increase Black and Hispanic women’s cancer diagnosis,
              mortality, and survival rates. However, what’s clear from available data is that
              compared to white women, Black women are developing{' '}
              <span className={classes.boldYellow}>breast cancer </span>
              earlier in life, developing more aggressive forms of breast cancer{' '}
              <sup style={{ fontFamily: 'ProximaNovaReg', fontSize: '.8rem' }}>6</sup>, and are more
              likely to die from the disease at every age
              <sup style={{ fontFamily: 'ProximaNovaReg', fontSize: '.8rem' }}>7</sup>. Similarly,
              both Black and Hispanic women are typically diagnosed at a later stage and have higher
              mortality rates due to cervical cancer. In fact, a
              <a
                href="https://www.contemporaryobgyn.net/view/addressing-the-cervical-cancer-screening-disparities-gap"
                className={classes.linkText}
                target="_blank"
                rel="sponsored"
              >
                {' '}
                Hologic-sponsored study{' '}
              </a>
              cites that the incident rate of cervical cancer among Hispanic women is 22% higher
              compared to white women, and that, in the U.S., Black women are twice as likely to die
              from <span className={classes.boldYellow}>cervical cancer</span> when compared to
              white women<sup style={{ fontFamily: 'ProximaNovaReg', fontSize: '.8rem' }}>8</sup>.
            </Typography>
          </Grid>

          {matchesSM ? mobileAudioCaption : desktopCap}
        </Grid>
      </Grid>
      <StatSection1 />
      <StatSection2 />
      <Typography variant="body1" className={classes.lastTextBox}>
        So why are Black and Hispanic women dying more often and at younger ages than other
        ethnicities? Correlative data seems to suggest that a number of social, geographic,
        environmental and economic disparities come into play.
      </Typography>
    </Grid>
  );
};

export default WhyDoes;
